import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Collapse } from 'react-bootstrap';
import { useLabels } from 'wsm-common-data';
import { formatCurrency } from 'ddc-intl';
import './styles.scss';

const MAX_SUBITEMS_DISPLAY = 4;

const SingleItem = ({
	index,
	itemName,
	subItems,
	itemType,
	itemPrice,
	totalItemsCount,
	isModal = false,
	onOpenModal,
	isFirstItem,
	isEnableDisclaimer,
	togglePackageDetails = false,
	layout = 'box'
}) => {
	const isListStyle = layout === 'list';
	const showPricing = useSelector((state) => state.settings.showPricing);
	const locale = useSelector((state) => state.requestData.locale);
	const labels = useLabels();
	const optionId = `packages-options-${itemType}-${index || ''}`;
	const [openDisclaimerObj, setIsOpenDisclaimer] = useState({});
	const [isExpanded, setIsExpanded] = useState(
		!isListStyle || !togglePackageDetails
	);
	const showListToggle = isListStyle && togglePackageDetails && !isModal;
	const icon = isExpanded ? 'collapse' : 'expand';
	const shouldDisplayOptionPricing = itemType === 'option' && showPricing;

	const onToggleExpand = useCallback(() => {
		setIsExpanded((prevIsExpanded) => !prevIsExpanded);
	}, []);

	const onOpenItemModal = useCallback(() => {
		if (!isModal) {
			onOpenModal();
			if (optionId && !isFirstItem) {
				// get offsetTop and scroll to Option item on modal
				setTimeout(() => {
					const optionItemModal = `${optionId}-modal`;
					const selectorOptionItemModal = document.querySelector(
						`#${optionItemModal}`
					);
					if (selectorOptionItemModal) {
						selectorOptionItemModal.scrollIntoView({
							behavior: 'smooth'
						});
					}
				}, 400);
			}
		}
	}, [isModal, onOpenModal, optionId, isFirstItem]);

	const onToggleDisclaimer = useCallback(
		(subName) => {
			const newOpenDisclaimerObj = { ...openDisclaimerObj };
			newOpenDisclaimerObj[subName] = !newOpenDisclaimerObj[subName];
			setIsOpenDisclaimer(newOpenDisclaimerObj);
		},
		[openDisclaimerObj]
	);

	const renderSubItems = (items = [], hasLink = false) => {
		const columnClasses =
			totalItemsCount === 1 && !isModal ? 'col-xs-12 col-md-6' : '';
		const containerClasses =
			totalItemsCount === 1 && !isModal ? 'row d-flex flex-wrap' : '';
		const collapseLink = hasLink ? (
			<div className="mt-2">
				<button
					type="button"
					onClick={onOpenItemModal}
					className="text-link text-muted text-decoration-underline mt-3"
				>
					{labels.get('ALL_INCLUDED_ITEMS')}
				</button>
			</div>
		) : null;
		return (
			<div>
				<ul className={`${containerClasses} pl-6 mb-0`}>
					{items.map((sub, subIndex) => {
						const subId = optionId + subIndex;
						const isDisplayDisclaimer =
							sub.disclaimer && isEnableDisclaimer;
						return (
							<li
								key={subId}
								className={`mb-3 pr-5 pl-0 ${columnClasses}`}
							>
								<div>
									<span className="mr-3">{sub.name}</span>
									{shouldDisplayOptionPricing &&
									sub.price > 0 ? (
										<span className="text-muted ddc-font-size-xsmall mr-3">
											{formatCurrency(sub.price, locale)}
										</span>
									) : (
										<></>
									)}
									{isDisplayDisclaimer ? (
										<span className="ddc-font-size-xsmall">
											<button
												className={`${
													openDisclaimerObj[subId]
														? 'd-none'
														: ''
												} btn-unstyled p-0 text-muted text-decoration-underline`}
												type="button"
												onClick={() =>
													onToggleDisclaimer(subId)
												}
											>
												{labels.get(
													'DISCLAIMER_OPTION'
												)}
											</button>
										</span>
									) : null}
								</div>
								{isDisplayDisclaimer ? (
									<Collapse in={openDisclaimerObj[subId]}>
										<div className="text-muted ddc-font-size-xsmall">
											<span className="mr-3">
												{sub.disclaimer}
											</span>
											<span>
												<button
													className="btn-unstyled p-0 text-muted text-decoration-underline"
													type="button"
													onClick={() =>
														onToggleDisclaimer(
															subId
														)
													}
												>
													{labels.get('CLOSE')}
												</button>
											</span>
										</div>
									</Collapse>
								) : null}
							</li>
						);
					})}
				</ul>
				{collapseLink}
			</div>
		);
	};

	if (!itemName) {
		return null;
	}
	let renderedOptions;
	let additionalOptions;
	if (!subItems || subItems.length === 0) {
		// do nothing
	} else if (subItems.length <= MAX_SUBITEMS_DISPLAY || isModal) {
		// render all options on modal or if 4 or less on main page
		renderedOptions = renderSubItems(subItems);
	} else {
		// render first 4 options and have link to show the rest on the modal
		const firstFourOptions = subItems.slice(0, MAX_SUBITEMS_DISPLAY);
		renderedOptions = renderSubItems(firstFourOptions, true);
	}
	const containerClasses = isListStyle
		? 'package-item border-bottom py-4 px-5'
		: 'h-100 bg-contrast-med p-5 d-flex flex-column option-item border-radius-md';

	return (
		<div
			className={containerClasses}
			id={isModal ? `${optionId}-modal` : optionId}
		>
			<div className="d-flex justify-content-between align-items-center">
				<div className="package-info h-100">
					{isListStyle ? (
						<h4 className="package-name font-weight-bold m-0">
							{itemName}
						</h4>
					) : (
						<h4 className="mb-3 mt-0 font-weight-bold">
							{itemName}
						</h4>
					)}

					{showPricing && itemPrice !== '$0' && itemPrice !== '0' ? (
						<div
							className={
								isListStyle
									? 'package-value ddc-font-size-small text-muted pt-2'
									: 'mb-3 mt-0 font-weight-bold line-height-reset'
							}
						>
							{isListStyle ? (
								`${itemPrice} ${
									(labels.get('VALUE') || 'value').toLowerCase()
								}`
							) : (
								<>
									{`${itemPrice} `}
									<span className="font-weight-normal text-muted ddc-font-size-xsmall">
										{itemType === 'package'
											? labels.get(
													'INCLUDED_IN_LISTING_PRICE'
												)
											: labels.get(
													'TOTAL_INCLUDED_IN_LISTING_PRICE'
												)}
									</span>
								</>
							)}
						</div>
					) : null}
				</div>
				{showListToggle && (
					<button
						type="button"
						className="package-action plus-button"
						onClick={onToggleExpand}
						aria-label={
							isExpanded
								? labels.get('CLOSE') || 'Collapse'
								: 'Expand'
						}
						aria-expanded={isExpanded}
					>
						<i
							className={`ddc-icon icon-style-collapse ddc-icon-${icon}`}
							role="presentation"
							aria-hidden="true"
							tabIndex={-1}
						/>
					</button>
				)}
			</div>

			{showListToggle ? (
				<Collapse in={isExpanded}>
					<div className="pt-4">
						{renderedOptions}
						{additionalOptions}
					</div>
				</Collapse>
			) : (
				<>
					{renderedOptions}
					{additionalOptions}
				</>
			)}
		</div>
	);
};

SingleItem.defaultProps = {
	itemName: undefined,
	subItems: [],
	itemPrice: '0',
	widgetInfo: {
		windowId: 'packages-options1',
		widgetName: 'ws-packages-options'
	},
	togglePackageDetails: false,
	layout: 'box'
};

SingleItem.propTypes = {
	index: PropTypes.number,
	itemType: PropTypes.string.isRequired,
	itemName: PropTypes.string,
	subItems: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	itemPrice: PropTypes.string,
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string,
		widgetName: PropTypes.string
	}),
	totalItemsCount: PropTypes.number,
	isModal: PropTypes.bool,
	onOpenModal: PropTypes.func.isRequired,
	isFirstItem: PropTypes.bool.isRequired,
	isEnableDisclaimer: PropTypes.bool.isRequired,
	togglePackageDetails: PropTypes.bool,
	layout: PropTypes.oneOf(['box', 'list'])
};

export default SingleItem;
